import React, { useEffect, useState } from "react";
import { Events } from "@/lib/api";
import { Radio, MapPin, Calendar as CalIcon } from "lucide-react";

function fmt(iso) {
    if (!iso) return { date: "", time: "" };
    const d = new Date(iso);
    return {
        date: d
            .toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
            })
            .toUpperCase(),
        time: d
            .toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                timeZoneName: "short",
            })
            .toUpperCase(),
    };
}

const typeIcon = {
    livestream: Radio,
    "in-person": MapPin,
    conference: CalIcon,
    event: CalIcon,
};

export default function EventsSection() {
    const [events, setEvents] = useState([]);
    useEffect(() => {
        Events.list().then(setEvents).catch(() => {});
    }, []);

    return (
        <section id="events" className="relative z-10" data-testid="events-section">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-16 md:pt-24">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="overline-cobalt mb-3">§ Ledger</div>
                        <h2
                            className="font-display"
                            style={{
                                fontSize: "clamp(36px, 5vw, 64px)",
                                lineHeight: 0.98,
                                color: "var(--ink)",
                            }}
                        >
                            Upcoming{" "}
                            <span
                                style={{
                                    fontFamily: "'Fraunces', serif",
                                    fontStyle: "italic",
                                    fontWeight: 400,
                                    color: "var(--cobalt)",
                                }}
                            >
                                gatherings
                            </span>
                            .
                        </h2>
                    </div>
                    <div className="overline hidden md:block">
                        [Coming soon]
                    </div>
                </div>

                {events.length === 0 ? (
                    <div className="panel p-12 md:p-16 text-center" data-testid="events-empty">
                        <div className="overline-cobalt mb-4">[Status · Pending]</div>
                        <h3
                            className="font-display"
                            style={{
                                fontSize: "clamp(28px, 3.4vw, 44px)",
                                lineHeight: 1.05,
                                color: "var(--ink)",
                            }}
                        >
                            Coming{" "}
                            <span
                                style={{
                                    fontFamily: "'Fraunces', serif",
                                    fontStyle: "italic",
                                    fontWeight: 400,
                                    color: "var(--cobalt)",
                                }}
                            >
                                soon.
                            </span>
                        </h3>
                        <p
                            className="mt-4 max-w-md mx-auto"
                            style={{
                                color: "var(--ink-2)",
                                fontSize: 14,
                                lineHeight: 1.65,
                            }}
                        >
                            Livestreams, dinners, and conferences will be
                            announced here. Subscribe to the newsletter to get
                            invitations first.
                        </p>
                    </div>
                ) : (
                    <ul className="panel" data-testid="events-list">
                        {events.map((e) => {
                            const { date, time } = fmt(e.date);
                            const Icon = typeIcon[e.event_type] || CalIcon;
                            return (
                                <li
                                    key={e.id}
                                    className="group grid grid-cols-12 gap-3 items-center px-5 py-5 border-b last:border-b-0 transition-colors hover:bg-[var(--panel-2)]"
                                    style={{ borderColor: "var(--rule-soft)" }}
                                >
                                    <div className="col-span-3 md:col-span-2 overline" style={{ color: "var(--cobalt)" }}>
                                        {date}
                                    </div>
                                    <div
                                        className="col-span-3 md:col-span-2 font-mono-tech"
                                        style={{ fontSize: 11, color: "var(--muted)" }}
                                    >
                                        {time}
                                    </div>
                                    <div className="col-span-6 md:col-span-5">
                                        <h3
                                            style={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontWeight: 700,
                                                fontSize: 18,
                                                color: "var(--ink)",
                                                lineHeight: 1.3,
                                                letterSpacing: "-0.01em",
                                            }}
                                        >
                                            {e.title}
                                        </h3>
                                        {e.description && (
                                            <p
                                                style={{
                                                    color: "var(--ink-2)",
                                                    fontSize: 13,
                                                    marginTop: 2,
                                                }}
                                                className="line-clamp-1"
                                            >
                                                {e.description}
                                            </p>
                                        )}
                                    </div>
                                    <div
                                        className="col-span-8 md:col-span-2 font-mono-tech uppercase flex items-center gap-1.5"
                                        style={{
                                            fontSize: 10.5,
                                            color: "var(--muted)",
                                            letterSpacing: "0.15em",
                                        }}
                                    >
                                        <Icon size={12} /> {e.location || e.event_type}
                                    </div>
                                    <div className="col-span-4 md:col-span-1 text-right">
                                        <a
                                            href={e.url || "#"}
                                            className="font-mono-tech opacity-0 group-hover:opacity-100 transition-opacity"
                                            style={{
                                                color: "var(--cobalt)",
                                                fontSize: 11,
                                                letterSpacing: "0.15em",
                                            }}
                                        >
                                            OPEN →
                                        </a>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </section>
    );
}
